import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { useSessionSwitcher } from "@/contexts/SessionSwitcherContext";
import { useGetEntityData } from "@/hooks/api/query/useGetEntityData";
import { useAuth } from "@/contexts/AuthContext";
import { queryKeys } from "@/lib/queryClient";
import { evaluateEntitySize } from "@/utils/size";
import {
  isContactDataComplete,
  isConfirmationsComplete,
} from "@/utils/contactValidation";
import { saveEntityData } from "@/utils/database";
import { findDuplicateCuiSession } from "@/utils/multi-session";
import {
  isArticle9Complete,
  isCompanySectionComplete,
  validateOnboardingFields,
} from "@/utils/onboardingValidation";
import { isRelatieStatComplete } from "@/utils/relatieStatValidation";
import {
  updateFormSessionProgress,
  completeFormSession,
} from "@/utils/session";
import { extractDataFromFiles } from "@/lib/file-extraction";
import { computeClassification } from "@/utils/classificationCalculations";
import { useFetchCompanyData } from "@/hooks/api/mutate/useFetchCompanyData";
import { useCheckActionCredits } from "@/hooks/api/query/useCheckActionCredits";
import { useDeductCredits } from "@/hooks/api/mutate/useDeductCredits";
import { CREDIT_ACTION_TYPES } from "@/constants/credit";
import { InsufficientCreditsError } from "@/utils/supabaseFunctionsError";
import type { CompanyDataResult } from "@/services/company-data-service";
import type { SurseDate } from "@/types/identificareFirma";
import type {
  ClassificationType,
  DocumenteAnexateData,
  EntityData,
  RelatieStatRomanData,
  ReprezentantRomania,
  SituatieServiciiUEData,
} from "@/types/entity";
import type {
  DocumentType,
  RomanianStateRelationshipData,
  EUServiceProvisionData,
  Article9Data,
} from "@/lib/file-extraction/types";
import type { ContactData, ContactPerson } from "@/types/contact";
import type { SelectedService } from "@/types/service";
import type { ImpactAnalysisCalculatedResult } from "@/types/impact-analysis";
import { DEFAULT_IMPACT_ANALYSIS } from "@/constants/analysis";

export type FieldDiff = {
  field: string;
  label: string;
  currentValue: string;
  newValue: string;
};

export type FetchCompanyDataResultStatus = {
  status: "idle" | "success" | "error";
  filledCount?: number;
  error?: string;
  surse?: SurseDate | null;
};

const COMPANY_FIELDS: { field: keyof EntityData; label: string }[] = [
  { field: "cui", label: "CUI" },
  { field: "nume_entitate", label: "Denumire entitate" },
  { field: "cif", label: "Nr. înregistrare" },
  { field: "adresa", label: "Adresa sediu social" },
  { field: "telefon", label: "Telefon" },
  { field: "caen_principal", label: "CAEN Principal" },
];

const FINANCIAL_FIELDS: { field: keyof EntityData; label: string }[] = [
  { field: "nr_salariati", label: "Nr. Angajați" },
  { field: "cifra_afaceri", label: "Cifra de Afaceri (EUR)" },
  { field: "active", label: "Active Totale (EUR)" },
];

const METADATA_FIELDS: (keyof EntityData)[] = [
  "original_cui_input",
  "clean_cui",
  "has_vat_registration",
  "anaf_vat_validated",
  "anaf_vat_validated_at",
  "anaf_data_fetched_at",
  "anaf_last_updated",
  "data_source",
  "original_cifra_afaceri_ron",
  "original_active_ron",
  "currency_conversion_rate",
  "currency_converted_at",
  "financial_data_year",
  "surse_date",
];

function formatFieldValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "";
  if (
    typeof value === "object" &&
    "cod" in (value as Record<string, unknown>)
  ) {
    const caen = value as { cod: string; denumire: string };
    return caen.cod
      ? `${caen.cod}${caen.denumire ? ` - ${caen.denumire}` : ""}`
      : "";
  }
  if (typeof value === "number") return value === 0 ? "" : String(value);
  return String(value);
}

function hasFieldValue(value: unknown): boolean {
  return formatFieldValue(value) !== "";
}

type ContactPersonKey =
  | "reprezentantLegal"
  | "responsabilSecuritate"
  | "contactOperational";

type ContactPersonField = keyof (ContactPerson & { functie: string });

const EMPTY_CONTACT_PERSON: ContactPerson & { functie: string } = {
  nume: "",
  prenume: "",
  email: "",
  telefon: "",
  functie: "",
};

function mapEvaluarePrelimToClassification(
  value: string
): ClassificationType | null {
  const lower = value.toLowerCase().trim();
  if (lower.includes("esen")) return "ESSENTIAL";
  if (lower.includes("important")) return "IMPORTANT";
  if (lower.includes("afara") || lower.includes("scope")) return "OUT_OF_SCOPE";
  return null;
}

const EMPTY_RELATIE_STAT: RelatieStatRomanData = {
  stabilita_in_romania: null,
  sediu_principal_in_romania: null,
  entitate_administratie_publica: null,
  entitate_critica_294_2024: null,
  entitate_extra_ue: null,
  reprezentant_romania: null,
};

const EMPTY_SITUATIE_SERVICII: SituatieServiciiUEData = {
  furnizeaza_servicii_romania: null,
  furnizeaza_servicii_ue: null,
};

const EMPTY_ARTICLE9: ImpactAnalysisCalculatedResult = {
  article9a: null,
  article9b: null,
  article9c: null,
  article9d: null,
};

const EMPTY_CONTACT_DATA: ContactData = {
  reprezentantLegal: { ...EMPTY_CONTACT_PERSON },
  responsabilSecuritate: { ...EMPTY_CONTACT_PERSON },
  contactOperational: { ...EMPTY_CONTACT_PERSON },
  adreseIP: [""],
  sistemeAfectate: "",
  confirmaExactitate: false,
  acceptaObligatii: false,
};

export function useOnboardingForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const {
    currentSessionId,
    refreshCurrentSession,
    sessions,
    archivedSessions,
    setShowPaywall,
  } = useSessionSwitcher();

  const { data: entityDataResponse, isLoading: isLoadingEntity } =
    useGetEntityData(currentSessionId, {
      enabled: !!currentSessionId && !!user,
    });

  const isFirstSession = sessions.length <= 1;

  const creditCheck = null;
  const isCreditCheckLoading = false;

  const { mutateAsync: deductCredits } = useDeductCredits();

  const existingEntity = entityDataResponse?.entity_data;

  const [entityData, setEntityData] = useState<Partial<EntityData>>({});
  const [contactData, setContactData] =
    useState<ContactData>(EMPTY_CONTACT_DATA);
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>(
    []
  );
  const [autoFilledFields, setAutoFilledFields] = useState<Set<string>>(
    new Set()
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [romanianStateRelationship, setRomanianStateRelationship] =
    useState<RomanianStateRelationshipData | null>(null);
  const [euServiceProvision, setEUServiceProvision] =
    useState<EUServiceProvisionData | null>(null);
  const [article9, setArticle9] = useState<Article9Data | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isExtractingFile, setIsExtractingFile] = useState(false);
  const [fetchCompanyDataResult, setFetchCompanyDataResult] = useState<
    Record<"company" | "financial", FetchCompanyDataResultStatus>
  >({ company: { status: "idle" }, financial: { status: "idle" } });
  const [manualClassification, setManualClassification] =
    useState<ClassificationType | null>(null);
  const [article9Enabled, setArticle9Enabled] = useState(false);
  const [article9Results, setArticle9Results] =
    useState<ImpactAnalysisCalculatedResult>({ ...EMPTY_ARTICLE9 });
  const [documenteAnexate, setDocumenteAnexate] =
    useState<DocumenteAnexateData | null>(null);

  const fetchCompanyDataMutation = useFetchCompanyData();

  // Initialize from existing data once loaded
  if (existingEntity && !isInitialized && !isLoadingEntity) {
    setEntityData(existingEntity);
    if (existingEntity.contact_data) {
      setContactData(existingEntity.contact_data);
    }
    if (existingEntity.selected_services) {
      setSelectedServices(existingEntity.selected_services);
    }
    if (existingEntity.nis_classification?.classificationV2) {
      setManualClassification(
        existingEntity.nis_classification.classificationV2 as ClassificationType
      );
    }
    if (existingEntity.impact_analysis?.result) {
      const result = existingEntity.impact_analysis.result;
      const hasValues =
        result.article9a !== null ||
        result.article9b !== null ||
        result.article9c !== null ||
        result.article9d !== null;
      if (hasValues) {
        setArticle9Results(result);
        setArticle9Enabled(true);
      }
    }
    if (existingEntity.documente_anexate) {
      setDocumenteAnexate(existingEntity.documente_anexate);
    }
    setIsInitialized(true);
  }

  // Derived values (computed inline, no useMemo)
  const sizeEvaluation = evaluateEntitySize(
    entityData.nr_salariati ?? 0,
    entityData.cifra_afaceri ?? 0,
    entityData.active ?? 0
  );

  const classificationResult = computeClassification(
    sizeEvaluation,
    selectedServices,
    article9Enabled,
    article9Results
  );

  // Compute without art9 data to determine if Art 9 is needed (avoids circular dependency)
  const baseClassificationResult = computeClassification(
    sizeEvaluation,
    selectedServices,
    false,
    EMPTY_ARTICLE9
  );

  const needsArticle9 = baseClassificationResult?.classificationV2 === "IMPACT";

  useEffect(() => {
    if (needsArticle9 && !article9Enabled) {
      setArticle9Enabled(true);
    }
  }, [needsArticle9, article9Enabled]);

  const sectionCompletion = {
    company: isCompanySectionComplete(entityData),
    financial: Boolean(
      entityData.nr_salariati && entityData.cifra_afaceri && entityData.active
    ),
    services: selectedServices.length > 0,
    relatie_stat: isRelatieStatComplete(entityData.relatie_stat_roman),
    servicii_ue: Boolean(
      entityData.situatie_servicii_ue &&
        entityData.situatie_servicii_ue.furnizeaza_servicii_romania !== null &&
        entityData.situatie_servicii_ue.furnizeaza_servicii_ue !== null &&
        (entityData.situatie_servicii_ue.furnizeaza_servicii_ue !== true ||
          ((entityData.situatie_servicii_ue.state_membre_ue ?? "").trim()
            .length > 0 &&
            (entityData.situatie_servicii_ue.adrese_ue ?? "").trim().length >
              0))
    ),
    classification: Boolean(manualClassification),
    contacts: isContactDataComplete(contactData),
    confirmations: isConfirmationsComplete(contactData),
    documente_anexate:
      documenteAnexate?.avem_documente != null &&
      (documenteAnexate.avem_documente !== true ||
        (Boolean(documenteAnexate.nr_documente?.trim()) &&
          Boolean(documenteAnexate.doc_dimensiune?.trim()))),
    article9: isArticle9Complete(article9Results),
  };

  const relevantCompletion = needsArticle9
    ? sectionCompletion
    : Object.fromEntries(
        Object.entries(sectionCompletion).filter(([key]) => key !== "article9")
      );
  const completedSections =
    Object.values(relevantCompletion).filter(Boolean).length;
  const completionPercentage = Math.round(
    (completedSections / Object.keys(relevantCompletion).length) * 100
  );

  const duplicateCuiMatch = findDuplicateCuiSession({
    cui: entityData.cui,
    sessions,
    archivedSessions,
    excludeSessionId: currentSessionId,
  });

  const isFormValid =
    sectionCompletion.company &&
    sectionCompletion.financial &&
    sectionCompletion.services &&
    sectionCompletion.contacts &&
    sectionCompletion.confirmations &&
    sectionCompletion.relatie_stat &&
    sectionCompletion.servicii_ue &&
    !duplicateCuiMatch;

  const isPaywallRequired =
    !isFirstSession &&
    !!creditCheck &&
    !creditCheck.already_purchased &&
    !creditCheck.has_credits;

  const clearFieldError = useCallback((key: string) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const updateEntityField = useCallback(
    (field: keyof EntityData, value: unknown) => {
      setEntityData((prev) => ({ ...prev, [field]: value }));
      clearFieldError(field);
    },
    [clearFieldError]
  );

  const updateContactField = useCallback(
    (person: ContactPersonKey, field: ContactPersonField, value: string) => {
      setContactData((prev) => ({
        ...prev,
        [person]: { ...prev[person], [field]: value },
      }));
      clearFieldError(`${person}.${field}`);
    },
    [clearFieldError]
  );

  const updateRelatieStatField = useCallback(
    (field: keyof RelatieStatRomanData, value: boolean | null) => {
      setEntityData((prev) => ({
        ...prev,
        relatie_stat_roman: {
          ...(prev.relatie_stat_roman ?? EMPTY_RELATIE_STAT),
          [field]: value,
        },
      }));
      clearFieldError(`relatie_stat_roman.${field}`);
    },
    [clearFieldError]
  );

  const updateSituatieServiciiField = useCallback(
    (field: keyof SituatieServiciiUEData, value: boolean | null) => {
      setEntityData((prev) => {
        const current = prev.situatie_servicii_ue ?? EMPTY_SITUATIE_SERVICII;
        const updated = { ...current, [field]: value };
        if (
          field === "furnizeaza_servicii_ue" &&
          (value === false || value === null)
        ) {
          updated.state_membre_ue = "";
          updated.adrese_ue = "";
        }
        return { ...prev, situatie_servicii_ue: updated };
      });
      clearFieldError(`situatie_servicii_ue.${field}`);
    },
    [clearFieldError]
  );

  const updateSituatieServiciiUEStates = useCallback(
    (value: string) => {
      setEntityData((prev) => ({
        ...prev,
        situatie_servicii_ue: {
          ...(prev.situatie_servicii_ue ?? EMPTY_SITUATIE_SERVICII),
          state_membre_ue: value,
        },
      }));
      clearFieldError("situatie_servicii_ue.state_membre_ue");
    },
    [clearFieldError]
  );

  const updateSituatieServiciiUEAddresses = useCallback(
    (value: string) => {
      setEntityData((prev) => ({
        ...prev,
        situatie_servicii_ue: {
          ...(prev.situatie_servicii_ue ?? EMPTY_SITUATIE_SERVICII),
          adrese_ue: value,
        },
      }));
      clearFieldError("situatie_servicii_ue.adrese_ue");
    },
    [clearFieldError]
  );

  const updateReprezentantRomania = useCallback(
    (field: keyof ReprezentantRomania, value: string) => {
      setEntityData((prev) => {
        const current = prev.relatie_stat_roman ?? EMPTY_RELATIE_STAT;
        const currentRep = current.reprezentant_romania ?? {
          denumire: "",
          adresa: "",
          email: "",
          telefon: "",
        };
        return {
          ...prev,
          relatie_stat_roman: {
            ...current,
            reprezentant_romania: { ...currentRep, [field]: value },
          },
        };
      });
      clearFieldError(`relatie_stat_roman.reprezentant_romania.${field}`);
    },
    [clearFieldError]
  );

  const updateContactMeta = useCallback(
    (
      field:
        | "adreseIP"
        | "sistemeAfectate"
        | "confirmaExactitate"
        | "acceptaObligatii",
      value: string[] | string | boolean
    ) => {
      setContactData((prev) => ({ ...prev, [field]: value }));
      clearFieldError(field);
    },
    [clearFieldError]
  );

  const updateDocumenteAnexate = useCallback((data: DocumenteAnexateData) => {
    setDocumenteAnexate(data);
    setFieldErrors((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        if (key.startsWith("documente_anexate.")) delete next[key];
      }
      return next;
    });
  }, []);

  const handleServicesChange = useCallback(
    (services: SelectedService[]) => {
      setSelectedServices(services);
      setManualClassification(null);
      setArticle9Results({ ...EMPTY_ARTICLE9 });
      setArticle9Enabled(false);
      if (services.length > 0) {
        clearFieldError("selectedServices");
      }
    },
    [clearFieldError]
  );

  const updateManualClassification = useCallback(
    (classification: ClassificationType) => {
      setManualClassification(classification);
      clearFieldError("manualClassification");
    },
    [clearFieldError]
  );

  const toggleArticle9 = useCallback(() => {
    setArticle9Enabled((prev) => !prev);
  }, []);

  const updateArticle9Result = useCallback(
    (field: keyof ImpactAnalysisCalculatedResult, value: unknown) => {
      setArticle9Results((prev) => ({ ...prev, [field]: value }));
      clearFieldError("article9");
    },
    [clearFieldError]
  );

  const validateForm = useCallback((): boolean => {
    setFieldErrors({});
    const validation = validateOnboardingFields({
      entityData,
      selectedServices,
      contactData,
      documenteAnexate,
      manualClassification,
      needsArticle9,
      article9Results,
    });

    if (!validation.isValid) {
      setFieldErrors(validation.fieldErrors);
      const errorCount = Object.keys(validation.fieldErrors).length;
      toast({
        title: `Completare incompletă. ${errorCount} ${errorCount === 1 ? "eroare găsită" : "erori găsite"}.`,
        variant: "destructive",
      });
      return false;
    }
    return true;
  }, [
    entityData,
    selectedServices,
    contactData,
    documenteAnexate,
    manualClassification,
    needsArticle9,
    article9Results,
  ]);

  const handleFileUpload = useCallback(async (files: File[]) => {
    if (files.length === 0) return;

    const fileName = files[0].name.toLowerCase();
    const documentType: DocumentType =
      fileName.endsWith(".xlsx") || fileName.endsWith(".xls")
        ? "notification"
        : "notification_decision";

    setIsExtractingFile(true);
    try {
      const result = await extractDataFromFiles({ files, documentType });

      if (!result.isValid || result.errors.length > 0) {
        toast({
          title: "Eroare la procesarea documentului. Verifică formatul.",
          variant: "destructive",
        });
        return;
      }

      const merged = result.mergedData;
      const filledFields = new Set<string>();

      if (merged.entityData && Object.keys(merged.entityData).length > 0) {
        const ed = merged.entityData;
        for (const key of Object.keys(ed)) {
          const value = ed[key as keyof typeof ed];
          if (value !== null && value !== undefined && value !== "") {
            filledFields.add(key);
          }
        }
        setEntityData((prev) => ({ ...prev, ...ed }));

        if ("selected_services" in ed) {
          const services = ed.selected_services ?? [];
          setSelectedServices(services);
          if (services.length > 0) {
            filledFields.add("selectedServices");
          }
        }

        const art9Result = (ed as Record<string, unknown>).impact_analysis as
          | { result?: ImpactAnalysisCalculatedResult }
          | undefined;
        if (art9Result?.result) {
          setArticle9Results(art9Result.result);
          setArticle9Enabled(true);
          filledFields.add("article9");
        }
      }

      const evaluarePrelim = result.fileResults
        .map(
          (fr) => fr.extractedData?.fields?.evaluare_preliminara?.parsedValue
        )
        .find((v) => v != null);
      if (evaluarePrelim && typeof evaluarePrelim === "string") {
        const classification =
          mapEvaluarePrelimToClassification(evaluarePrelim);
        if (classification) {
          setManualClassification(classification);
          filledFields.add("manualClassification");
        }
      }

      if (merged.contactData && Object.keys(merged.contactData).length > 0) {
        setContactData((prev) => ({ ...prev, ...merged.contactData }));
        for (const key of Object.keys(merged.contactData)) {
          filledFields.add(`contact_${key}`);
        }
      }

      if (merged.romanianStateRelationship) {
        setRomanianStateRelationship(merged.romanianStateRelationship);
        filledFields.add("romanianStateRelationship");
      }

      if (merged.euServiceProvision) {
        setEUServiceProvision(merged.euServiceProvision);
        filledFields.add("euServiceProvision");
      }

      if (merged.article9) {
        setArticle9(merged.article9);
        filledFields.add("article9");
      }

      if (merged.notificationDecision) {
        const nd = merged.notificationDecision;
        const mapped: Partial<EntityData> = {};
        if (nd.cui) {
          mapped.cui = nd.cui;
          filledFields.add("cui");
        }
        if (nd.entityName) {
          mapped.nume_entitate = nd.entityName;
          filledFields.add("nume_entitate");
        }
        if (nd.address) {
          mapped.adresa = nd.address;
          filledFields.add("adresa");
        }
        setEntityData((prev) => ({ ...prev, ...mapped }));
      }

      setAutoFilledFields(filledFields);
      setTimeout(() => setAutoFilledFields(new Set()), 2000);

      const fieldCount = filledFields.size;
      toast({ title: `${fieldCount} câmpuri extrase din document` });
    } catch {
      toast({
        title: "Eroare la procesarea documentului.",
        variant: "destructive",
      });
    } finally {
      setIsExtractingFile(false);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!currentSessionId) {
      toast({ title: "Nu există o sesiune activă.", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      const savedClassification = classificationResult
        ? {
            ...classificationResult,
            classificationV2:
              manualClassification ?? classificationResult.classificationV2,
          }
        : null;

      const savedImpactAnalysis = article9Enabled
        ? {
            ...DEFAULT_IMPACT_ANALYSIS,
            section1_mandatory: {
              ...DEFAULT_IMPACT_ANALYSIS.section1_mandatory,
              is_sole_essential_provider: article9Results.article9a,
              formally_designated: article9Results.article9d,
            },
            result: article9Results,
          }
        : null;

      const fullEntityData = {
        ...entityData,
        dimensiune: sizeEvaluation?.size ?? null,
        selected_services: selectedServices,
        contact_data: contactData,
        documente_anexate: documenteAnexate,
        nis_classification: savedClassification,
        impact_analysis: savedImpactAnalysis,
      } as EntityData;

      const success = await saveEntityData(currentSessionId, fullEntityData);

      if (success) {
        await updateFormSessionProgress(currentSessionId, 4, 0);
        await completeFormSession(currentSessionId);

        await queryClient.invalidateQueries({
          queryKey: queryKeys.entityData.bySession(currentSessionId),
        });
        await queryClient.invalidateQueries({
          queryKey: queryKeys.sessions.all,
        });
        await refreshCurrentSession();

        toast({ title: "Datele au fost salvate cu succes!" });
        navigate("/dashboard");
      } else {
        toast({
          title: "Eroare la salvarea datelor. Încearcă din nou.",
          variant: "destructive",
        });
      }
    } catch {
      toast({ title: "Eroare la salvarea datelor.", variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  }, [
    currentSessionId,
    isFirstSession,
    creditCheck,
    deductCredits,
    setShowPaywall,
    entityData,
    sizeEvaluation,
    classificationResult,
    manualClassification,
    article9Enabled,
    article9Results,
    selectedServices,
    contactData,
    documenteAnexate,
    queryClient,
    navigate,
    refreshCurrentSession,
  ]);

  const getFieldDiffs = useCallback(
    (
      variant: "company" | "financial",
      result: CompanyDataResult
    ): FieldDiff[] => {
      const fields = variant === "company" ? COMPANY_FIELDS : FINANCIAL_FIELDS;
      const fetched = result.entityData as Partial<EntityData> | undefined;
      if (!fetched) return [];

      const diffs: FieldDiff[] = [];
      for (const { field, label } of fields) {
        const currentVal = formatFieldValue(entityData[field]);
        const newVal = formatFieldValue(fetched[field]);
        if (newVal && currentVal && currentVal !== newVal) {
          diffs.push({
            field,
            label,
            currentValue: currentVal,
            newValue: newVal,
          });
        }
      }
      return diffs;
    },
    [entityData]
  );

  const fetchCompanyDataForSection = useCallback(
    async (
      variant: "company" | "financial",
      cui: string
    ): Promise<CompanyDataResult | null> => {
      setFetchCompanyDataResult((prev) => ({
        ...prev,
        [variant]: { status: "idle" },
      }));
      try {
        return await fetchCompanyDataMutation.mutateAsync({ cui });
      } catch (eroare) {
        setFetchCompanyDataResult((prev) => ({
          ...prev,
          [variant]: {
            status: "error",
            error:
              eroare instanceof Error
                ? eroare.message
                : "Eroare la preluarea datelor",
          },
        }));
        return null;
      }
    },
    [fetchCompanyDataMutation]
  );

  const applyCompanyData = useCallback(
    (
      variant: "company" | "financial",
      selectedFields: string[],
      result: CompanyDataResult
    ) => {
      const fetched = result.entityData as Partial<EntityData> | undefined;
      if (!fetched) return;

      const fields = variant === "company" ? COMPANY_FIELDS : FINANCIAL_FIELDS;
      const selectedSet = new Set(selectedFields);
      const filledFields = new Set<string>();
      const updates: Partial<EntityData> = {};

      for (const { field } of fields) {
        const newVal = fetched[field];
        if (!hasFieldValue(newVal)) continue;

        const currentVal = entityData[field];
        if (!hasFieldValue(currentVal) || selectedSet.has(field)) {
          (updates as Record<string, unknown>)[field] = newVal;
          filledFields.add(field);
        }
      }

      const metadataUpdates: Partial<EntityData> = {};
      for (const field of METADATA_FIELDS) {
        if (fetched[field] !== undefined) {
          (metadataUpdates as Record<string, unknown>)[field] = fetched[field];
        }
      }

      if (result.anafData) {
        (metadataUpdates as Record<string, unknown>).anaf_raw_data =
          result.anafData;
      }

      setEntityData((prev) => ({ ...prev, ...metadataUpdates, ...updates }));

      setAutoFilledFields(filledFields);
      setTimeout(() => setAutoFilledFields(new Set()), 3000);

      const count = filledFields.size;
      setFetchCompanyDataResult((prev) => ({
        ...prev,
        [variant]: {
          status: "success",
          filledCount: count,
          surse: result.surseDate ?? null,
        },
      }));
      setTimeout(
        () =>
          setFetchCompanyDataResult((prev) => ({
            ...prev,
            [variant]: { status: "idle" },
          })),
        3000
      );
    },
    [entityData]
  );

  return {
    // State
    entityData,
    contactData,
    selectedServices,
    romanianStateRelationship,
    euServiceProvision,
    article9,
    autoFilledFields,
    fieldErrors,
    manualClassification,
    article9Enabled,
    article9Results,
    documenteAnexate,

    // Loading states
    isLoadingEntity,
    isExtractingFile,
    isSaving,
    isCreditCheckLoading: !isFirstSession && isCreditCheckLoading,

    // Derived values
    sizeEvaluation,
    classificationResult,
    needsArticle9,
    sectionCompletion,
    completionPercentage,
    isFormValid,
    duplicateCuiMatch,
    isPaywallRequired,
    openPaywall: () => setShowPaywall(true),

    // Actions
    updateEntityField,
    updateContactField,
    updateContactMeta,
    updateRelatieStatField,
    updateSituatieServiciiField,
    updateSituatieServiciiUEStates,
    updateSituatieServiciiUEAddresses,
    updateReprezentantRomania,
    handleServicesChange,
    handleFileUpload,
    handleSave,
    validateForm,
    updateDocumenteAnexate,
    updateManualClassification,
    toggleArticle9,
    updateArticle9Result,

    // Company data fetch
    fetchCompanyDataMutation,
    fetchCompanyDataForSection,
    fetchCompanyDataResult,
    getFieldDiffs,
    applyCompanyData,
  };
}
